<template>
  <div class="payment-print-invoice-akt m-0 w-100">
    <div class="d-flex gap-3 align-content-center flex-wrap"
         :class="{
      'justify-content-between': !justMadePayment,
      'justify-content-end': justMadePayment
    }">

      <div class="d-flex align-content-center gap-3"
           v-if="!justMadePayment && showPPOCheckbox">

        <CheckboxGroup :label="$t('fiscal_cheque')"
                       :fid="`ppo-checkbox`"
                       class="text-left sm mb-0 d-flex align-items-center"
                       :readonly="forcePPO !== null"
                       v-model="ppoModel"/>

        <CheckboxGroup :label="$t('electronic_cheque')"
                       v-if="kkmEnabled && ppo"
                       class="text-left sm mb-0 d-flex align-items-center"
                       :fid="`electronic_cheque`"
                       v-model="sendFiscalToEmailModel"/>


        <SelectGroup :options="aqsiAccounts"
                     v-if="fiscalIsAQSI && aqsiAccounts.length > 1 && ppo"
                     :null-option="false"
                     variant="white"
                     :label="$t('AQSI_account')"
                     class="mb-0 d-flex align-items-center"
                     inputClass="sm"
                     :nullOptionTitle="$t('AQSI_account')"
                     :translate="false"
                     valuekey="key"
                     title="title"
                     :showlabel="false"
                     v-model="ppoAccountModel"/>
      </div>

      <div class="d-flex align-content-center gap-2 flex-wrap">
        <div v-if="forVisit">
          <a @click="printInvoice"
             class="btn-themed btn-themed-squared btn-themed-squared-rounder btn-themed-outline d-inline-block">{{ $t('invoice_print') }}</a>
        </div>
        <div v-if="forVisit">
          <a @click="startPrintAkt"
             class="btn-themed btn-themed-squared btn-themed-squared-rounder btn-themed-outline d-inline-block">
            {{ $t('akt_print') }}
          </a>
        </div>
        <slot/>
      </div>


    </div>


    <OptionsPopup v-if="optionsModalOpen"
                  :title="$t('choose_signer')"
                  @changed="modalResolve"
                  :options="signerOptions"/>
    <AktOptionsPopup :default-date="visit ? visit.start : null"
                     @changed="modalResolve"/>
  </div>
</template>

<script setup>
import JwtService from "@/services/jwt.service"
import {computed, getCurrentInstance, nextTick, onBeforeMount, ref, inject} from "vue"
import {useStore} from "vuex"
import OptionsPopup from "@/components/parts/general/OptionsPopup.vue"
import AktOptionsPopup from "@/components/parts/patients/AktOptionsPopup.vue"
import CheckboxGroup from "@/components/form/CheckboxGroup.vue"
import SelectGroup from "@/components/form/SelectGroup.vue"

const props = defineProps({
  forVisit: {
    type: Boolean,
    default: true
  },
  ppo: {
    type: Boolean,
  },
  sendFiscalToEmail: {
    type: Boolean,
  },
  ppoAccount:{

  },
  forcePPO: {
    type: Boolean,
  },
  justMadePayment: {
    type: Boolean,
  },
})
const emit = defineEmits(['update:ppo', 'update:sendFiscalToEmail', 'update:ppoAccount'])
const paymentMethodObjects = inject("paymentMethodObjects")

const ppoModel = computed({
  get: () => props.ppo,
  set: (val) => emit('update:ppo', val)
})
const sendFiscalToEmailModel = computed({
  get: () => props.sendFiscalToEmail,
  set: (val) => emit('update:sendFiscalToEmail', val)
})
const ppoAccountModel = computed({
  get: () => props.ppoAccount,
  set: (val) => emit('update:ppoAccount', val)
})

const store = useStore()
let bvModal = null
let i18n = null

const optionsModalOpen = ref(false)
const modalResolve = ref(() => {})

onBeforeMount(() => {
  const instance = (getCurrentInstance())
  bvModal = instance?.ctx?._bv__modal
  i18n = instance?.ctx?.$i18n
})

const clinic = computed(() => store.state.auth.clinic)
const visit = computed(() => store.state.patient.visit)
const patient = computed(() => store.state.patient.patient)
const kkmEnabled = computed(() => store.getters.kkmEnabled)
const rights = computed(() => store.state.auth.rights)
const cashdeskSystemSelected = computed(() => store.getters.cashdeskSystemSelected)
const fiscalIsAQSI = computed(() => cashdeskSystemSelected.value === 'aqsi')
const aqsiAccounts = computed(() => store.getters.aqsiAccounts)
const canCheckPPO = computed(() => paymentMethodObjects.value.every(obj => Number(obj.pay_method) !== 7))
const showPPOCheckbox = computed(() => {
  if(!rights.value.includes('cashdesk')) return false
  if(!canCheckPPO.value) return false
  if(!props.forVisit && !kkmEnabled.value) return false
  return true
})

const hasRepresentative = computed(() => {
  return patient.value?.representative_id || patient.value?.representative_fio
})

const  signerOptions = computed(() => {
  return [
    { id: 'patient', title: i18n.t('patient') },
    { id: 'representative', title: i18n.t('representative_noun') },
  ]
})

const printInvoice = async () => {
  let patientSigner = ''
  if(hasRepresentative.value) {
    patientSigner = await openOptionsModal()
    if(!patientSigner) return
  }
  let link = JwtService.getPrintPrelink(clinic.value)
  window.open(`${link}/invoice/${visit.value.id}?patientSigner=${patientSigner}`, '_blank')
}

const startPrintAkt = async () => {
  const res = await getOptionsForAkt()
  if(!res || !res.signer || !res.date) return
  await printAkt(res.signer, res.date)
}

const getOptionsForAkt = async () => {
  bvModal.show('akt-options-popup-modal')
  return new Promise((resolve) => {
    modalResolve.value = resolve
  })
}

const printAkt = async (signer, date = '') => {
  let patientSigner = ''
  if(hasRepresentative.value) {
    patientSigner = await openOptionsModal()
    if(!patientSigner) return
  }
  let link = JwtService.getPrintPrelink(clinic.value)
  window.open(`${link}/akt/${visit.value.id}?signer=${signer}&patientSigner=${patientSigner}&date=${date}`, '_blank')
}

const openOptionsModal = async () => {
  optionsModalOpen.value = true
  await nextTick()
  bvModal.show('options-popup-modal')
  return new Promise((resolve) => {
    modalResolve.value = resolve
  });
}
</script>

<style lang="scss" scoped>
@media screen and (max-width: 768px) {
  .payment-print-invoice-akt {
    .btn-themed {
      padding: 0 15px;
    }
  }
}
</style>
